/**
 * UnifiedDetailPage - Combined content detail page for all content types
 * 
 * Handles: movie, series, program content types
 * 
 * Features:
 * - Shared DetailHeroScene
 * - Movie: ticketing, subscription, QR popover
 * - Series/Program: Episode selector
 * - Common: Artist slider, related content, description
 */
import {
    Box,
    Button,
    Typography,
    Collapse,
    Popover,
    CircularProgress,
} from "../legacy-ui/materialCompat";
import { FOCUS_BORDER } from "../../config/focusStyles";
import { PlayCircleIcon } from "../legacy-ui/icons";
import { PendingIcon } from "../legacy-ui/icons";
import {
    FocusContext,
    useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { hasAuthenticatedSession } from "../../services/authStorage";

// Common components
import DetailHeroScene from "../common/DetailHeroScene";
import ContentAction from "../common/contentAction";
import ArtistSlider from "../common/ArtistSlider";

// Series-specific
import SeriesEpisodes from "../series/seriesEpisodes";
import SeriesRelatedMovies from "../series/relatedMovies";

// Movie-specific
import MovieRelatedMovies from "../movie/relatedMovies";

// Services & Utils
import httpService from "../../services/http.service";
import { getBillingApiRoute, getCMSApiRoute } from "../../utils/api.utils";
import { getContentTypeName, getUserSubscriptionStatus } from "../../utils/data.utils";
import smoothScroll from "../../utils/animateScroll";
import { getVerticalFocusScrollTarget } from "../../utils/focusScroll";
import { preloadPlayerData } from "../../utils/hooks/usePlayerDataCache";
import { setAutoResumeCookie } from "../../utils/autoResumeCookie";
import { useInputMode } from "../../utils/hooks/useInputMode";
import {
    getContentMode,
    normalizeContentKind,
    sendContentView,
    sendPlayIntent,
} from "../../services/mediaAnalyticsService";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
    getLiveStartTimestamp,
    isLiveContent,
} from "../../utils/liveStartCountdown";
import {
    getServerNow,
    subscribePublicAuthConfig,
} from "../../services/publicAuthConfigService";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

const ScrollingRows = React.forwardRef(function ScrollingRows(props, ref) {
    return <div {...props} ref={ref} className={"legacy-detail-scroll" + (props.className ? " " + props.className : "")} />;
});

const RelatedContentSection = function (props) {
    return <div {...props} className={"legacy-detail-related-section" + (props.className ? " " + props.className : "")} />;
};

const RelatedContentTitle = function (props) {
    return <h3 {...props} className={"legacy-detail-related-title" + (props.className ? " " + props.className : "")} />;
};

// ============================================================
// Auto-Redirect Components
// ============================================================

const RedirectNotification = function (props) {
    return <div {...props} className={"legacy-detail-redirect" + (props.className ? " " + props.className : "")} />;
};

const RedirectText = function (props) {
    return <div {...props} className={"legacy-detail-redirect-text" + (props.className ? " " + props.className : "")} />;
};

const RedirectTimerBadge = function (props) {
    return <span {...props} className={"legacy-detail-redirect-timer" + (props.className ? " " + props.className : "")} />;
};

const CancelRedirectButton = React.forwardRef(function CancelRedirectButton(props, ref) {
    return <button {...props} ref={ref} className={"legacy-detail-redirect-cancel" + (props.className ? " " + props.className : "")} />;
});

const CancelButton = ({ onEnterPress, onFocus }) => {
    const { ref, focused, focusSelf } = useFocusable({
        onFocus,
        onEnterPress,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    return (
        <CancelRedirectButton
            ref={ref}
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            style={{
                background: focused ? "#FFFFFF" : "rgba(255, 255, 255, 0.1)",
                color: focused ? "#EF4444" : "#FFFFFF",
                border: focused ? "2px solid #FFFFFF" : "1px solid rgba(255, 255, 255, 0.2)",
            }}
        >
            لغو
        </CancelRedirectButton>
    );
};

// ============================================================
// Shared Button Components
// ============================================================


const PlayButton = ({ onEnterPress, label, onFocus, loading, autofocus = false, focusKey }) => {
    const { ref, focused, focusSelf } = useFocusable({
        focusKey,
        onEnterPress,
        onFocus,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    useEffect(() => {
        if (autofocus) focusSelf();
    }, [autofocus, focusSelf]);

    return (
        <Button
            ref={ref}
            type="button"
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            className="cursor-pointer"
            sx={{
                minWidth: 0,
                height: "72px",
                backgroundColor: focused ? "rgba(255,255,255,0.95)" : "rgba(180, 30, 30, 0.75)",
                fontSize: "32px",
                fontWeight: 600,
                lineHeight: "32px",
                color: focused ? "#D32F2F" : "#F0F0F0",
                borderRadius: "15px",
                padding: "0 24px",
                border: focused ? "2px solid #050505" : "2px solid rgba(255,255,255,0.25)",
                boxShadow: focused
                    ? "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)"
                    : "0 4px 16px rgba(0,0,0,0.4)",
                textShadow: focused ? "none" : "0 1px 2px rgba(0,0,0,0.8)",
                marginLeft: "12px",
                transition: "all 0.15s ease",
                boxSizing: "border-box",
                whiteSpace: "nowrap",
                WebkitAppearance: "none",
                outline: "none",
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color: "#D32F2F",
                    border: "2px solid #050505",
                    boxShadow: "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)",
                    textShadow: "none",
                },
            }}
            disabled={loading}
        >
            <div className="flex justify-center items-center">
                {loading ? (
                    <CircularProgress size={32} sx={{ color: focused ? "#D32F2F" : "#FFF", opacity: 0.8 }} />
                ) : (
                    <React.Fragment>
                        <PlayCircleIcon sx={{ marginLeft: "6px", fontSize: "32px" }} />
                        <Typography fontSize={30} fontWeight={600}>
                            {label || "تماشا کن"}
                        </Typography>
                    </React.Fragment>
                )}
            </div>
        </Button>
    );
};

const PlayButtonWithPopover = ({ label, onFocus, children, focusKey }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setIsOpen] = useState(Boolean(anchorEl));

    const handleClick = (event) => {
        setAnchorEl(open ? null : event.currentTarget);
        setIsOpen(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsOpen(false);
    };

    const { ref, focused, focusSelf } = useFocusable({
        focusKey,
        onFocus: (props) => {
            onFocus(props);
            setAnchorEl(ref.current);
            setIsOpen(true);
        },
        onBlur: () => {
            setAnchorEl(null);
            setIsOpen(false);
        },
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnterPopover = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    useEffect(() => {
        focusSelf();
    }, [focusSelf]);

    useEffect(() => {
        setAnchorEl(ref.current);
        setIsOpen(focused);
    }, [focused]);

    return (
        <React.Fragment>
            <Button
                onClick={handleClick}
                onMouseEnter={handleMouseEnterPopover}
                ref={ref}
                type="button"
                sx={{
                    minWidth: 0,
                    backgroundColor: "#6A0000",
                    height: '72px',
                    fontSize: "32px",
                    fontWeight: 500,
                    lineHeight: "32px",
                    color: "#F0F0F0",
                    ml: 2,
                    borderRadius: "24px",
                    padding: "0 32px",
                    border: open ? "2px solid #050505" : FOCUS_BORDER.inactive,
                    boxShadow: open
                        ? "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)"
                        : "0 4px 16px rgba(0,0,0,0.4)",
                    "&:hover": {
                        backgroundColor: "rgb(80,0,0)",
                        border: "2px solid #050505",
                        boxShadow: "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)",
                    },
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    WebkitAppearance: "none",
                    outline: "none",
                }}
            >
                <PlayCircleIcon sx={{ marginLeft: "5%" }} />
                <Typography minWidth={"fit-content"} fontSize={25} fontWeight={700}>
                    {label || "تماشا کن"}
                </Typography>
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "center",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "center",
                    horizontal: "right",
                }}
                slotProps={{
                    paper: {
                        style: {
                            padding: 20,
                            maxWidth: 400,
                            background: "#121212",
                            borderRadius: "24px",
                            color: "whitesmoke",
                        },
                    },
                }}
            >
                <Box>{children}</Box>
            </Popover>
        </React.Fragment>
    );
};

const PreviewButton = ({ onEnterPress, onFocus, focusKey }) => {
    const { ref, focused, focusSelf } = useFocusable({
        focusKey,
        onFocus,
        onEnterPress,
    });
    const { setMouseFocus } = useInputMode();
    const handleMouseEnter = useCallback(() => {
        setMouseFocus(true);
        focusSelf();
    }, [focusSelf, setMouseFocus]);

    return (
        <Button
            ref={ref}
            type="button"
            onClick={onEnterPress}
            onMouseEnter={handleMouseEnter}
            className="cursor-pointer"
            sx={{
                width: "auto",
                height: "72px",
                background: focused ? "rgba(255,255,255,0.95)" : "rgba(180, 30, 30, 0.75)",
                fontSize: "32px",
                fontWeight: 600,
                color: focused ? "#D32F2F" : "#F0F0F0",
                borderRadius: "15px",
                padding: "0 24px",
                border: focused ? "2px solid #050505" : "2px solid rgba(255,255,255,0.25)",
                boxShadow: focused
                    ? "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)"
                    : "0 4px 16px rgba(0,0,0,0.4)",
                textShadow: focused ? "none" : "0 1px 2px rgba(0,0,0,0.8)",
                marginLeft: "12px",
                transition: "all 0.15s ease",
                boxSizing: "border-box",
                whiteSpace: "nowrap",
                WebkitAppearance: "none",
                outline: "none",
                "&:hover": {
                    background: "rgba(255,255,255,0.95)",
                    color: "#D32F2F",
                    border: "2px solid #050505",
                    boxShadow: "0 0 0 3px #FFFFFF, 0 0 0 7px #050505, 0 4px 16px rgba(0,0,0,0.4)",
                    textShadow: "none",
                },
            }}
        >
            <Typography fontSize={32} fontWeight={600}>
                پیش‌نمایش
            </Typography>
        </Button>
    );
};

// ============================================================
// Main Component
// ============================================================

const UnifiedDetailPage = ({ content = {} }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isLogin = hasAuthenticatedSession();
    const { ref, focusKey } = useFocusable();

    // Content type detection
    const contentKind = content?.contentType?.kind || "movie";
    const isSeries = contentKind === "series" || contentKind === "program";
    // isMovie now includes ALL non-series content (movie, music, etc.)
    const isMovie = !isSeries;
    const entryPointGuide = NextPlayGuideHelper.getGuideFromNavigationState(location.state);

    const navigateWithEntryPointGuide = (path, options) => {
        if (!entryPointGuide) {
            if (options) {
                navigate(path, options);
            } else {
                navigate(path);
            }
            return;
        }

        navigate(path, {
            ...(options || {}),
            state: NextPlayGuideHelper.withGuideNavigationState(
                options?.state || location.state,
                entryPointGuide
            ),
        });
    };

    // Movie-specific state
    const [isPurchased, setIsPurchased] = useState(false);
    const [cinemaTicketSlug, setCinemaTicketSlug] = useState("");
    const [canPlay, setCanPlay] = useState();
    const [canPlayLoading, setCanPlayLoading] = useState(true);

    // Countdown states for livestreams
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [hadActiveCountdown, setHadActiveCountdown] = useState(false);
    const [autoRedirectCounter, setAutoRedirectCounter] = useState(null);
    const [clockRevision, setClockRevision] = useState(0);

    const handleWatchEventRef = React.useRef(null);
    useEffect(() => {
        handleWatchEventRef.current = handleWatchEvent;
    });

    useEffect(() => {
        return subscribePublicAuthConfig(() => {
            setClockRevision((value) => value + 1);
        });
    }, []);

    useEffect(() => {
        const liveStartDate = getLiveStartTimestamp(content);

        if (!isLiveContent(content) || !liveStartDate) {
            setHadActiveCountdown(false);
            setTimeRemaining(null);
            return;
        }

        const targetTime = liveStartDate;
        
        const updateCountdown = () => {
            const now = getServerNow();
            const diff = targetTime - now;
            if (diff <= 0) {
                setTimeRemaining(0);
                return true;
            } else {
                setTimeRemaining(diff);
                return false;
            }
        };

        const isFinished = updateCountdown();
        if (isFinished) {
            setHadActiveCountdown(false);
            return;
        }

        setHadActiveCountdown(true);

        const interval = setInterval(() => {
            const finished = updateCountdown();
            if (finished) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [content, clockRevision]);

    useEffect(() => {
        if (timeRemaining === 0 && hadActiveCountdown) {
            setAutoRedirectCounter(5);

            const interval = setInterval(() => {
                setAutoRedirectCounter((prev) => {
                    if (prev === null) return null;
                    if (prev <= 1) {
                        clearInterval(interval);
                        if (handleWatchEventRef.current) {
                            handleWatchEventRef.current();
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        } else {
            setAutoRedirectCounter(null);
        }
    }, [timeRemaining, hadActiveCountdown]);

    // Input mode for scroll behavior
    const { isMouseFocus, isMouseFocusRef } = useInputMode();

    // Scroll handler
    const onRowFocus = useCallback(
        (props) => {
            // Skip scroll adjustment when focus originated from mouse hover
            if (isMouseFocusRef.current) return;
            if (!ref.current) return;

            const targetScroll = getVerticalFocusScrollTarget(ref.current, props || {}, {
                topPadding: 72,
                bottomPadding: 120,
                align: "center",
            });
            smoothScroll(ref, targetScroll, 500, isMouseFocus);
        },
        [ref, isMouseFocus, isMouseFocusRef]
    );

    // ============================================================
    // Movie-specific logic
    // ============================================================

    const getCinemaTicketData = async () => {
        try {
            const { slug } = content;
            const response = await httpService.get(getCMSApiRoute("cinema-ticket"), {
                params: {
                    pageIndex: 1,
                    pageNumber: 1,
                    contentKey: decodeURIComponent(slug),
                },
            });

            if (response.status === 200) {
                const { data } = response.data;
                setCinemaTicketSlug(data?.results[0]?.slug);
                await isCinemaTicketPurchased(data.results[0]?.slug, slug);
            }
        } catch (error) {
            console.log(`Error on CinemaTicket Get: ${error}`);
        }
    };

    const isCinemaTicketPurchased = async (slug, contentSlug) => {
        try {
            const response = await httpService.get(getBillingApiRoute("payment"), {
                params: {
                    pageNumber: 1,
                    pageIndex: 40,
                    orderType: "cinema_ticket",
                    status: 1,
                    ticketKey: slug,
                },
            });

            if (response.data?.data?.results.length !== 0) {
                setIsPurchased(true);
            }
        } catch (error) {
            console.log(`Error on CinemaTicket purchase check: ${error}`);
        }
    };

    const reportContentWatch = async () => {
        try {
            await httpService.post(getCMSApiRoute("action/watch"), {
                contentKey: content.slug,
            });
        } catch (error) {
            console.log(error);
        }
    };

    const getPlayUrl = () => {
        return "/watch/" + content.slug;
    };

    const handleWatchEvent = async () => {
        // Check if user has watch history for this content (re-entry scenario)
        // If so, set autoResume cookie before navigating to player (skip for live streams)
        const isLiveStream = content?.isLive === 1 || content?.isLive === true;
        const analyticsPayload = {
            contentKey: content.slug,
            mode: getContentMode(content),
            contentKind: normalizeContentKind(content),
            surface: "content_detail",
            source: "primary_play_button",
        };

        const trackAllowedPlayIntent = () => {
            sendPlayIntent(analyticsPayload);
        };

        if (!isLiveStream && (content.watchInfo?.elapsedTime > 0 || content.options?.elapsedTime > 0)) {
            setAutoResumeCookie({
                slug: content.slug,
                season: null,
                episode: null,
            });
            console.log('[AutoResume] Cookie set for re-entry:', content.slug);
        }

        try {
            if (content.isTicketing === 1) {
                if (isPurchased === false) {
                    if (!isLogin) {
                        navigateWithEntryPointGuide(`/login?ref=${encodeURIComponent(window.location.pathname)}`);
                    }
                } else {
                    if (!isLogin) {
                        navigateWithEntryPointGuide(`/login?ref=${encodeURIComponent(`/watch/onlinebakhish/${content.slug}/${cinemaTicketSlug}/1`)}`);
                    } else {
                        trackAllowedPlayIntent();
                        navigateWithEntryPointGuide(`/watch/onlinebakhish/${content.slug}/${cinemaTicketSlug}/1`);
                    }
                }
            } else {
                if (content.isFree === 1) {
                    if (!isLogin) {
                        navigateWithEntryPointGuide(`/login?ref=${encodeURIComponent(getPlayUrl())}`);
                    } else {
                        trackAllowedPlayIntent();
                        await reportContentWatch();
                        navigateWithEntryPointGuide(getPlayUrl());
                    }
                } else {
                    if (!isLogin) {
                        navigateWithEntryPointGuide(`/login?ref=${encodeURIComponent(window.location.pathname)}`);
                    } else {
                        if (!canPlay) {
                            trackGoogleAnalyticsEvent("purchase_started", {
                                purchase_type: "subscription",
                                source: "content_detail_play_button",
                                content_id: content.slug,
                                content_type: normalizeContentKind(content),
                            });
                            navigate("/subscription");
                        } else {
                            trackAllowedPlayIntent();
                            await reportContentWatch();
                            navigateWithEntryPointGuide(getPlayUrl());
                        }
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getPlayLabel = () => {
        if (!isLogin) {
            if (content.isTicketing !== 1 || isPurchased === true) {
                return "ورود و تماشا";
            } else {
                return "ورود و خرید بلیط";
            }
        } else {
            if (content.isTicketing !== 1 || isPurchased === true) {
                if (content.isFree === 1 || canPlay) {
                    return "تماشا کن";
                } else {
                    return "خرید اشتراک";
                }
            } else {
                return "خرید بلیط";
            }
        }
    };

    const getCountdownComponent = () => {
        if (!timeRemaining) return null;
        const liveStartDate = getLiveStartTimestamp(content);

        return (
            <div style={{ display: "flex", alignItems: "center", height: 72, marginLeft: 12 }}>
                <LiveStartCountdown
                    startTimestamp={liveStartDate}
                    size="detail"
                    style={{ justifyContent: "center" }}
                />
            </div>
        );
    };

    const renderRedirectNotification = () => {
        if (autoRedirectCounter === null) return null;

        return (
            <RedirectNotification>
                <RedirectText>
                    <span>پخش زنده آغاز شد! انتقال به پخش در</span>
                    <RedirectTimerBadge>{autoRedirectCounter}</RedirectTimerBadge>
                    <span>ثانیه...</span>
                </RedirectText>
                <CancelButton
                    onEnterPress={() => setAutoRedirectCounter(null)}
                    onFocus={onRowFocus}
                />
            </RedirectNotification>
        );
    };

    const getMovieWatchButton = () => {
        if (timeRemaining !== null && timeRemaining > 0) {
            return getCountdownComponent();
        }

        const renderNormalPlayButton = () => {
            if (!isLogin) {
                if (content.isTicketing !== 1 || isPurchased === true) {
                    return (
                        <PlayButton
                            focusKey="detail-play-login"
                            label={"ورود و تماشا"}
                            onEnterPress={handleWatchEvent}
                            onFocus={onRowFocus}
                            loading={canPlayLoading}
                            autofocus={true}
                        />
                    );
                } else {
                    return (
                        <PlayButton
                            focusKey="detail-play-ticket-login"
                            label={"ورود و خرید بلیط"}
                            onEnterPress={handleWatchEvent}
                            onFocus={onRowFocus}
                            loading={canPlayLoading}
                            autofocus={true}
                        />
                    );
                }
            } else {
                if (content.isTicketing !== 1 || isPurchased === true) {
                    if (content.isFree === 1) {
                        return (
                            <PlayButton
                                focusKey="detail-play-free"
                                label={"تماشا کن"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    } else if (content.isFree !== 1 && canPlay) {
                        return (
                            <PlayButton
                                focusKey="detail-play-subscribed"
                                label={"تماشا کن"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    } else {
                        return (
                            <PlayButton
                                focusKey="detail-play-subscribe"
                                label={"خرید اشتراک"}
                                onEnterPress={handleWatchEvent}
                                onFocus={onRowFocus}
                                loading={canPlayLoading}
                                autofocus={true}
                            />
                        );
                    }
                } else {
                    return (
                        <PlayButtonWithPopover
                            focusKey="detail-play-ticket"
                            label={"خرید بلیط"}
                            onFocus={onRowFocus}
                            children={
                                <div className="relative">
                                    <p className="text-lg">
                                        جهت ادامه فرآیند خرید بلیط سینما آنلاین لطفا کد زیر را با تلفن
                                        همراه خود اسکن کنید و باقی مراحل را انجام دهید
                                    </p>
                                    <QRCode
                                        size={256}
                                        style={{
                                            height: "auto",
                                            maxWidth: "100%",
                                            width: "100%",
                                            padding: "15px",
                                        }}
                                        value={"https://manabakh.ir/movie/" + content.slug}
                                        viewBox={`0 0 256 256`}
                                        bgColor="#BFBFBF"
                                        fgColor="#121212"
                                    />
                                </div>
                            }
                        />
                    );
                }
            }
        };

        if (autoRedirectCounter !== null) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {renderNormalPlayButton()}
                    {renderRedirectNotification()}
                </div>
            );
        }

        return renderNormalPlayButton();
    };

    // ============================================================
    // Series-specific logic
    // ============================================================

    const getSeriesActionButton = () => {
        // Series don't show play button in hero - episodes have individual play buttons
        return null;
    };

    // ============================================================
    // Shared logic
    // ============================================================

    const getPreviewButton = () => {
        if (content?.trailer && content?.trailer !== null && content?.trailer !== "") {
            return (
                <PreviewButton
                    focusKey="detail-preview"
                    onEnterPress={() => navigate(`/trailer/${content.slug}`)}
                    onFocus={onRowFocus}
                />
            );
        }
        return null;
    };

    // ============================================================
    // Effects
    // ============================================================

    useEffect(() => {
        if (isMovie && content.isTicketing === 1) {
            getCinemaTicketData();
        }
    }, [content.slug, isMovie]);

    // Preload player data for faster playback (1-hour TTL cache)
    useEffect(() => {
        if (content?.slug) {
            preloadPlayerData(content.slug, contentKind);
        }
    }, [content?.slug, contentKind]);

    useEffect(() => {
        if (!content?.slug) return;

        sendContentView({
            contentKey: content.slug,
            mode: getContentMode(content),
            contentKind: normalizeContentKind(content),
            surface: "content_detail",
        });
    }, [content?.slug]);

    useEffect(() => {
        if (isMovie && isLogin) {
            getUserSubscriptionStatus().then((data) => {
                setCanPlay(data);
                setCanPlayLoading(false);
            });
        } else if (isMovie) {
            setCanPlayLoading(false);
        }
    }, [isLogin, isMovie]);

    // ============================================================
    // Render
    // ============================================================

    const RelatedContent = isMovie ? MovieRelatedMovies : SeriesRelatedMovies;

    return (
        <FocusContext.Provider value={focusKey}>
            <ScrollingRows ref={ref}>
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Hero Scene */}
                    <DetailHeroScene
                        content={content}
                        onPlay={isMovie ? handleWatchEvent : undefined}
                        onTrailer={() => navigate(`/trailer/${content.slug}`)}
                        playLabel={isMovie ? getPlayLabel() : undefined}
                        isPlayLoading={isMovie ? canPlayLoading : false}
                        isSeries={isSeries}
                        onRowFocus={onRowFocus}
                        renderButtons={() => (
                            <>
                                {isMovie && getMovieWatchButton()}
                                {isSeries && getSeriesActionButton()}
                                {getPreviewButton()}
                                <div className="flex justify-end">
                                    <ContentAction contentKey={content.slug} onFocus={onRowFocus} isMultiEpisode={isSeries} />
                                </div>
                            </>
                        )}
                    />

                    {/* Series Episodes (only for series/program) */}
                    {isSeries && content?.contentType?.seriesInfo && (
                        <SeriesEpisodes
                            seasons={content?.contentType?.seriesInfo?.sort(
                                (a, b) => a.episodeNumber - b.episodeNumber
                            )}
                            slug={content?.slug}
                            onFocus={onRowFocus}
                            movie={content}
                        />
                    )}

                    {/* Artist Slider */}
                    {content?.artists && content.artists.length > 0 && (
                        <ArtistSlider
                            artists={content.artists}
                            onFocus={onRowFocus}
                            title="هنرمندان"
                        />
                    )}

                    {/* Related Content Section */}
                    <RelatedContentSection>
                        <RelatedContentTitle>
                            {`${getContentTypeName(content?.contentType?.kind)} های پیشنهادی`}
                        </RelatedContentTitle>
                    </RelatedContentSection>
                    <RelatedContent
                        slug={content?.slug}
                        onFocus={onRowFocus}
                        genreKeys={content.genreKeys}
                        categoryKeys={content.categoryKeys}
                        kind={content?.contentType?.kind}
                    />
                </Box>
            </ScrollingRows>
        </FocusContext.Provider>
    );
};

export default UnifiedDetailPage;
